import { Injectable, signal, WritableSignal } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter, take } from 'rxjs';
import { LocationData } from '../model/LocationData';
import { countryCookie, readCookie } from '../utils/cookie';
import { AnalyticsService } from './analytics.service';
import { BrandMetadataService } from './brand-metadata.service';
import { BrowserPlatformService } from './browser-platform.service';
import { ConstantsService } from './constants.service';
import { DeviceCompatibilityService } from './device-compatibility.service';
import { UserLocationService } from './user-location.service';

@Injectable({ providedIn: 'root' })
export class AppStartupService {
  readonly routePath: WritableSignal<string>;
  readonly siteBrand = signal('');
  readonly locationData = signal<LocationData | undefined>(undefined);
  readonly isPc = signal(false);

  constructor(
    private readonly router: Router,
    private readonly userLocation: UserLocationService,
    private readonly deviceCompatibility: DeviceCompatibilityService,
    private readonly constants: ConstantsService,
    private readonly analytics: AnalyticsService,
    private readonly brandMetadata: BrandMetadataService,
    private readonly browser: BrowserPlatformService
  ) {
    this.routePath = signal(this.browser.location.pathname);
  }

  initialize(): void {
    this.analytics.initialize();
    this.applyPathBrand();
    this.applyRequestedBrand();

    const brand = this.constants.getSiteBrand();
    this.siteBrand.set(brand);
    this.brandMetadata.setOpenGraphImage(brand);

    if (this.browser.location.search.includes('brand=audibel')) {
      this.browser.navigateExternal('https://phonesupport.audibel.com/');
      return;
    }

    const cachedCountryCode = readCookie('locationData');
    if (cachedCountryCode) {
      this.routeWhenReady(cachedCountryCode);
      return;
    }

    this.userLocation.getUserCountry().pipe(take(1)).subscribe({
      next: locationData => {
        this.locationData.set(locationData);
        const expires = new Date(Date.now() + 60 * 60 * 1000);
        this.browser.cookie = countryCookie(locationData.countryCode, expires);
        this.routeWhenReady(locationData.countryCode);
      },
      error: () => this.routeWhenReady(undefined)
    });
  }

  private routeWhenReady(countryCode: string | undefined): void {
    if (this.router.navigated) {
      this.routeForCountry(countryCode);
      return;
    }

    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      take(1)
    ).subscribe(() => this.routeForCountry(countryCode));
  }

  private applyPathBrand(): void {
    const path = this.browser.location.pathname;
    const PATH_BRAND_MAP: Record<string, string> = {
      '/nuear': 'nuear',
      '/audiosync': 'audiosync',
      '/MyStarkey': 'starkey',
      '/MyAudibel': 'audibel',
      '/HearCircle': 'hearcircle',
      '/MySoundGear': 'soundgear',
      '/MyPhantom': 'phantom',
      '/Thrive': 'thrive',
    };
    const match = Object.keys(PATH_BRAND_MAP).find(prefix => path === prefix || path.startsWith(prefix + '/'));
    if (match) {
      this.constants.setSiteBrand(PATH_BRAND_MAP[match]);
    }
  }

  private applyRequestedBrand(): void {
    const requestedBrand = new URLSearchParams(this.browser.location.search).get('siteBrand')?.toLowerCase();
    const supportedBrands = [
      'starkey', 'audibel', 'hearcircle', 'kindilink3', 'soundgear', 'phantom',
      'afflelou', 'ouiezen', 'thrive', 'kindilink2', 'iproai', 'hearshare',
      'kindilinkassist'
    ];
    if (requestedBrand && supportedBrands.includes(requestedBrand)) {
      this.constants.setSiteBrand(requestedBrand);
    }
  }

  private routeForCountry(countryCode: string | undefined): void {
    if (this.shouldRejectDesktopCompatibilityRoute()) {
      this.routeDesktopToUnsupported();
      this.isPc.set(true);
    } else if (
      countryCode === 'DE' &&
      this.browser.location.href.includes('hearcircle') &&
      !this.browser.location.href.includes('nuear')
    ) {
      this.constants.setSiteBrand('nuear');
      this.siteBrand.set(this.constants.getSiteBrand());
      void this.router.navigate(['nuear']);
    } else if (this.browser.location.pathname === '/') {
      void this.router.navigate(['aidTypeForm']);
    }
  }

  private isAccountManagementRoute(): boolean {
    const path = this.browser.location.pathname.toLowerCase().replace(/[^a-z]/g, '');
    return ['deleteaccount', 'accessdata', 'landing', 'success', 'failure']
      .some(segment => path.includes(segment));
  }

  private shouldRejectDesktopCompatibilityRoute(): boolean {
    const path = this.browser.location.pathname.toLowerCase();
    const sharedRoute = [
      'aidtypeform',
      'compatibility',
      'pccompatibility',
      'pcnotsupported',
      'downloadpage'
    ].some(segment => path.includes(segment));

    return !this.deviceCompatibility.checkIfMobileDevice() &&
      !this.isAccountManagementRoute() &&
      !sharedRoute;
  }

  private routeDesktopToUnsupported(): void {
    const brand = this.constants.getSiteBrand();
    const requestedApplication = new URLSearchParams(this.browser.location.search).get('App');
    void this.router.navigate(['compatibility'], {
      state: {
        isCareGiver: false,
        compatibilityStatement: this.constants.PHONE_NOT_SUPPORTED,
        downloadStatement: '',
        appLink: '',
        icon: this.constants.UNRECOGNIZED_DEVICE_ICON_NAME,
        brand
      },
      queryParams: {
        App: requestedApplication || this.browser.location.pathname.split('/')[1] || 'PhoneSupport'
      },
      queryParamsHandling: 'merge'
    });
  }
}
